import c from 'classnames';
import { lazy, Suspense } from 'react';
import { Route, Routes } from 'react-router-dom';

import {
  ButtonColors,
  ButtonSizes,
  ButtonVariants,
  LinkButton,
  Spinfinity,
  Typography,
} from 'components';

import { SeedingSample } from './SeedingSample';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import styles from './Seeding.module.css';

const CSectionNICU = lazy(() => import('./PieCharts/c-section-NICU.svg?react'));
const CSectionNoNICU = lazy(
  () => import('./PieCharts/c-section-no-NICU.svg?react'),
);
const VaginalFewerInterventions = lazy(
  () => import('./PieCharts/vaginal-fewer-interventions.svg?react'),
);
const VaginalMoreInterventions = lazy(
  () => import('./PieCharts/vaginal-more-interventions.svg?react'),
);
export const SeedingRoutes = () => {
  return (
    <Routes>
      <Route path={``} element={<Seeding />} />
    </Routes>
  );
};
export const Seeding = () => {
  return (
    <section id='seeding' className={styles.page}>
      <Breadcrumb className={'pb-3 pt-3'} />
      <Typography as='h2' variant='heading-xl' className={styles.title}>
        Microbiome Seeding
      </Typography>
      <Typography as='p' variant='body-s'>
        The microbes you pass to baby set the stage for their gut development.
        Let’s take a look.
      </Typography>

      <Typography as='h3' variant='heading-l' className={styles.sectionTitle}>
        Early microbiome seeding
      </Typography>
      <Typography as='p' variant='body-s'>
        Your baby’s earliest sample will show those pioneer microbes that seeded
        from mom during delivery. But microbes can continue to be passed from
        mom during breastfeeding, and those first microbes from mom may expand
        over time.
      </Typography>

      <div className={styles.sample}>
        <Suspense fallback={<Spinfinity />}>
          <SeedingSample />
        </Suspense>
      </div>

      <Typography as='h3' variant='heading-l' className={styles.sectionTitle}>
        What impacts seeding at birth?
      </Typography>
      <Typography as='p' variant='body-s'>
        Birth mode has the largest demonstrated effect. Vaginal deliveries seed
        more microbes from mom to baby than C-sections.
        <sup>
          <LinkButton
            label='1'
            href='https://pubmed.ncbi.nlm.nih.gov/31534227'
            external
            variant={ButtonVariants.WRAPPER}
            size={ButtonSizes.S}
          />
        </sup>
      </Typography>

      <div className={c('flex justify-content-center', styles.pieCharts)}>
        <div>
          <Typography as='div' variant='body-s'>
            Vaginal fewer interventions
          </Typography>
          <VaginalFewerInterventions />

          <Typography as='div' variant='body-s'>
            Vaginal more interventions
          </Typography>
          <VaginalMoreInterventions />
        </div>
        <div>
          <Typography as='div' variant='body-s'>
            C-section Baby, no NICU
          </Typography>
          <CSectionNoNICU />
          <Typography as='div' variant='body-s'>
            C-section Baby in NICU
          </Typography>
          <CSectionNICU />
        </div>
      </div>

      <Typography as='h3' variant='heading-l' className={styles.sectionTitle}>
        Other factors that may have an impact:
      </Typography>

      <ul>
        <li>
          <Typography as='p' variant='body-s'>
            Antibiotics during labor
            <sup>
              <LinkButton
                label='2'
                href='https://pubmed.ncbi.nlm.nih.gov/26412384/'
                external
                variant={ButtonVariants.WRAPPER}
                size={ButtonSizes.S}
              />
            </sup>
          </Typography>
        </li>
        <li>
          <Typography as='p' variant='body-s'>
            Location (hospital, birthing center, home)
            <sup>
              <LinkButton
                label='3'
                href='https://pubmed.ncbi.nlm.nih.gov/32212591/'
                external
                variant={ButtonVariants.WRAPPER}
                size={ButtonSizes.S}
              />
              {', '}
              <LinkButton
                label='4'
                href='https://pubmed.ncbi.nlm.nih.gov/30353125/'
                external
                variant={ButtonVariants.WRAPPER}
                size={ButtonSizes.S}
              />
            </sup>
          </Typography>
        </li>
        <li>
          <Typography as='p' variant='body-s'>
            Interventions during labor
          </Typography>
        </li>
      </ul>
      <Typography as='p' variant='body-s'>
        After birth, beneficial microbes such as bifidobacteria can transfer to
        baby via breastfeeding.
        <sup>
          <LinkButton
            label='5'
            href='https://pubmed.ncbi.nlm.nih.gov/33897650/'
            external
            variant={ButtonVariants.WRAPPER}
            size={ButtonSizes.S}
          />
        </sup>
        {' You can '}
        <LinkButton
          label='learn more about seeding on our Tiny Blog'
          href='https://www.tinyhealth.com/blog/microbiome-seeding-at-birth'
          external
          variant={ButtonVariants.TEXT}
          size={ButtonSizes.M}
          color={ButtonColors.PURPLE}
        />
        {'.'}
      </Typography>
    </section>
  );
};
